// @flow
import * as _ from 'lodash';
import { $apply, $each, $set, update } from 'qim';

import {
  commonErrors as Errors,
  resourceNames,
} from '@zapier/toolbox-redux-framework';
import { addParamsToUrl, addTruthyParamsToUrl } from '@zapier/url-utils';
import {
  fetch,
  fetchJson,
  getJsonWithAccountId,
} from '@zapier/toolbox-browser-fetch';
import { getZapUrl, sortNodes } from '@zapier/product-editor';

import type { Flow } from './types';

type UnpauseOriginType =
  | 'editor: beta'
  | 'editor: simple zap setup'
  | 'dashboard';

type IsTemplate = { isTemplate: boolean };

type FolderIdType = number | string | 'trash' | 'held';

const API_BASE = '/api/v3';

const TRASH_FOLDER_ID = 'trash';
const TRASH_DAYS = 30;

const arrayToObject = (objects?: any) => {
  if (!objects) {
    return {};
  }

  return _.fromPairs(
    objects.map(item => {
      return [item.id, item];
    })
  );
};

const getZaps = (accountId, path, params = {}) => {
  return getJsonWithAccountId(
    accountId,
    addParamsToUrl(`${API_BASE}/${path}`, params)
  )
    .then(zaps =>
      _.fromPairs(
        zaps.objects.map(zap => {
          const nodes = arrayToObject(
            zap.nodes.map(node =>
              _.omit(
                {
                  ...node,
                  authentication_id: node.auth_id,
                },
                ['auth_id']
              )
            )
          );
          return [
            zap.id,
            {
              ...zap,
              authentication_id: zap.auth_id,
              nodes,
              nodeIds: sortNodes(zap.nodes).map(node => node.id),
            },
          ];
        })
      )
    )
    .catch(response => {
      throw new Errors.DataGetError({
        resource: resourceNames.zaps,
        response,
      });
    });
};

const objectifyNodes = (flow: ?Flow): Flow => {
  return update(['nodes', $apply(arrayToObject)], flow);
};

const Data = {
  zaps(accountId: string): Promise<Flow[]> {
    return Promise.all([
      Data.untrashedZaps(accountId),
      Data.trashedZaps(accountId),
    ]).then(([untrashed, trashed]) => ({
      ...untrashed,
      ...trashed,
    }));
  },

  untrashedZaps(accountId: string) {
    return getZaps(accountId, 'zaps');
  },

  trashedZaps(accountId: string) {
    return getZaps(accountId, 'zaps/trash', { trash_days: TRASH_DAYS }).then(
      update([$each, 'folder_ids', $set([TRASH_FOLDER_ID])])
    );
  },

  unpause(flowId: string, origin: UnpauseOriginType) {
    return fetch(fetch.url(`${API_BASE}/nodes/${flowId}/unpause`), {
      method: 'PUT',
      body: JSON.stringify({
        origin,
      }),
    })
      .then(fetch.success)
      .catch(response => {
        throw new Errors.UnpauseError({
          flowId,
          response,
        });
      });
  },

  patchZap(
    id: string,
    attrs: { title: string },
    { isTemplate }: IsTemplate = {}
  ): Promise<?Flow> {
    const url = addTruthyParamsToUrl(getZapUrl({ id, isTemplate }));
    return fetchJson(url, {
      method: 'PATCH',
      body: JSON.stringify(attrs),
    }).catch(response => {
      throw new Errors.DataPatchError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  transferZap(
    id: string,
    attrs: { customuserId: number },
    { isTemplate }: IsTemplate = {}
  ): Promise<?Flow> {
    const url = addTruthyParamsToUrl(getZapUrl({ id, isTemplate }));
    const body = { customuser_id: attrs.customuserId };
    return fetchJson(url, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }).catch(response => {
      throw new Errors.DataPatchError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  moveZap(zapId: string, folderId: FolderIdType) {
    return fetchJson(`${API_BASE}/zaps/${zapId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        folder_id: folderId,
      }),
    })
      .then(objectifyNodes)
      .catch(response => {
        throw new Errors.DataPutError({
          resource: resourceNames.zap,
          response,
        });
      });
  },

  deleteZap(zapId: string) {
    return fetchJson(`${API_BASE}/zaps/${zapId}`, {
      method: 'DELETE',
    }).catch(response => {
      throw new Errors.DataDeleteError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  deleteZaps(zapIds: string[]) {
    return fetchJson(`${API_BASE}/zaps`, {
      method: 'DELETE',
      body: JSON.stringify({ zap_ids: zapIds }),
    }).catch(response => {
      throw new Errors.DataDeleteError({
        resource: resourceNames.zap,
        response,
      });
    });
  },

  pause(flowId: string) {
    return fetch(fetch.url(`${API_BASE}/nodes/${flowId}/pause`), {
      method: 'PUT',
    })
      .then(fetch.success)
      .catch(response => {
        throw new Errors.PauseError({
          flowId,
          response,
        });
      });
  },

  copyZap(zap: Flow) {
    return fetchJson(`${API_BASE}/nodes/${zap.id}/copy`, {
      method: 'POST',
      body: JSON.stringify(zap),
    }).then(objectifyNodes);
  },
};

export default Data;
